import { useCallback, useMemo, useState } from 'react';
import {
  FlatList,
  ListRenderItem,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import { useQuery } from '@apollo/client/react';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { BaseModalProps, useModal } from '@/contexts/ModalContext';
import type {
  MuscleTargetRecord,
  SportActivityCategoryRecord,
  SportActivityRecord,
} from '~/powersync/generated/schema';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
} from '@/components/ui/drawer';
import { useTranslation } from 'react-i18next';
import {
  SportActivitySelectorSearch,
  type SportActivitySearchState,
} from '../sport-activity-selector-search/SportActivitySelectorSearch';
import { SportActivityFormDialog } from '~/sport-activity/components/sport-activity-form-dialog/SportActivityFormDialog';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { ExercisePicker } from '@/lib/local-api/sport-activity/exercise-picker';
import { useLocalizedValue } from '@/hooks/useLocalizedValue';
import { CurrentUserDocument } from '~/graphql/generated/graphql';

const MUSCLE_GROUPS = [
  'CHEST',
  'BACK',
  'LEGS',
  'ARMS',
  'SHOULDERS',
  'CORE',
  'CARDIOVASCULAR',
] as const;

type MuscleGroupFilter = (typeof MUSCLE_GROUPS)[number] | 'all';

const INITIAL_SEARCH_STATE: SportActivitySearchState = {
  results: [],
  isLoading: true,
  hasError: false,
};

export interface SportActivitySelectorModalProps extends BaseModalProps {
  onSportActivitySelected?: (sportActivity: SportActivityRecord) => void;
  portalHost?: string;
}

export const SportActivitySelectorModal = ({
  onClose,
  onSportActivitySelected,
  portalHost,
}: SportActivitySelectorModalProps) => {
  const { t } = useTranslation('workout');
  const { showModal } = useModal();
  const [searchState, setSearchState] =
    useState<SportActivitySearchState>(INITIAL_SEARCH_STATE);
  const [activeFilter, setActiveFilter] = useState<MuscleGroupFilter>('all');

  const handleStateChange = useCallback((state: SportActivitySearchState) => {
    setSearchState(state);
  }, []);

  // Batched hydration for the whole result set — one live query for all muscle
  // targets and one for all categories, instead of two per row.
  const resultIds = useMemo(
    () => searchState.results.map((r) => r.id),
    [searchState.results],
  );

  const { data: muscleTargets, isLoading: isLoadingMuscleTargets } =
    useLivePowerSyncQuery<MuscleTargetRecord>(
      db.SportActivity.queries.findMuscleTargetsBySportActivityIds(resultIds),
      [resultIds],
    );

  const { data: categories } =
    useLivePowerSyncQuery<SportActivityCategoryRecord>(
      db.SportActivity.queries.findAllCategories(),
      [],
    );

  const { data: userData } = useQuery(CurrentUserDocument);
  const currentUserId = userData?.currentUser?.id;

  const muscleTargetsByActivityId = useMemo(
    () => ExercisePicker.groupMuscleTargets(muscleTargets ?? []),
    [muscleTargets],
  );

  const categoriesById = useMemo(() => {
    const map = new Map<string, SportActivityCategoryRecord>();
    (categories ?? []).forEach((c) => map.set(c.id, c));
    return map;
  }, [categories]);

  // Filter is applied to the data array, so filtered-out exercises are never
  // mounted (no per-row `return null`).
  const visibleResults = useMemo(
    () =>
      ExercisePicker.filterByMuscleGroup(
        searchState.results,
        muscleTargetsByActivityId,
        activeFilter,
      ),
    [searchState.results, muscleTargetsByActivityId, activeFilter],
  );

  // While a specific muscle group is active, treat the batched muscle-target
  // query as part of loading so an active filter shows skeletons rather than a
  // premature "no results" before targets hydrate.
  const isLoading =
    searchState.isLoading || (activeFilter !== 'all' && isLoadingMuscleTargets);

  const viewState = ExercisePicker.resolveViewState({
    isLoading,
    hasError: searchState.hasError,
    resultCount: visibleResults.length,
  });

  const handleOpenCreateDialog = useCallback(() => {
    showModal(
      SportActivityFormDialog,
      {
        onSportActivitySaved: (id: string) => {},
      },
      { portalHost },
    );
  }, [showModal, portalHost]);

  const handleActivitySelect = useCallback(
    (activity: SportActivityRecord) => {
      onSportActivitySelected?.(activity);
      onClose?.();
    },
    [onSportActivitySelected, onClose],
  );

  const renderActivityItem: ListRenderItem<SportActivityRecord> = useCallback(
    ({ item }) => (
      <SportActivityListItem
        activity={item}
        muscleTargets={muscleTargetsByActivityId.get(item.id) ?? EMPTY_TARGETS}
        category={
          item.categoryId ? categoriesById.get(item.categoryId) : undefined
        }
        currentUserId={currentUserId}
        onSelect={handleActivitySelect}
        portalHost={portalHost}
      />
    ),
    [
      muscleTargetsByActivityId,
      categoriesById,
      currentUserId,
      handleActivitySelect,
      portalHost,
    ],
  );

  const renderSeparator = useCallback(
    () => <View className="h-px bg-border mx-5" />,
    [],
  );

  return (
    <Drawer open={true} onOpenChange={() => onClose()}>
      <DrawerContent>
        <DrawerHeader onClose={onClose}>
          <Text className="text-lg font-sora-semibold text-text-primary">
            {t('sportActivitySelector.title')}
          </Text>
        </DrawerHeader>

        <DrawerBody>
          {/* Search & Filters */}
          <View className="gap-3 px-5 pb-3">
            <SportActivitySelectorSearch onStateChange={handleStateChange} />

            {/* Filter tags */}
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerClassName="flex-row gap-2"
            >
              <Pressable
                testID="exercise-selector-create-custom"
                onPress={handleOpenCreateDialog}
                className="flex-row items-center gap-1 rounded-lg bg-surface-elevated px-3 py-2 active:opacity-70"
              >
                <Icon name="Plus" size={12} className="text-text-primary" />
                <Text className="text-xs font-inter-medium text-text-primary">
                  {t('sportActivitySelector.createCustom')}
                </Text>
              </Pressable>
              <Pressable
                onPress={() => setActiveFilter('all')}
                className={`rounded-lg px-3 py-2 ${
                  activeFilter === 'all' ? 'bg-accent' : 'bg-surface-elevated'
                }`}
              >
                <Text
                  className={`text-xs font-inter-medium ${
                    activeFilter === 'all'
                      ? 'text-accent-text'
                      : 'text-text-muted'
                  }`}
                >
                  {t('sportActivitySelector.filters.all')}
                </Text>
              </Pressable>
              {MUSCLE_GROUPS.map((group) => {
                const isActive = activeFilter === group;
                return (
                  <Pressable
                    key={group}
                    onPress={() => setActiveFilter(group)}
                    className={`rounded-lg px-3 py-2 ${
                      isActive ? 'bg-accent' : 'bg-surface-elevated'
                    }`}
                  >
                    <Text
                      className={`text-xs font-inter-medium ${
                        isActive ? 'text-accent-text' : 'text-text-muted'
                      }`}
                    >
                      {t(`muscleGroup.${group}`)}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </View>

          {/* Results — `min-h-0 overflow-hidden` bounds this flex region inside the
              maxHeight-capped drawer on web. `min-h-0` lets it shrink below its content
              (web flex items default to min-height:auto and otherwise grow past the drawer);
              `overflow-hidden` clips. The results FlatList scrolls within these bounds; the
              skeleton (a plain View) is clipped instead of spilling out of the drawer. */}
          <View className="flex-1 min-h-0 overflow-hidden">
            {viewState === 'loading' && <ExercisePickerSkeleton />}

            {viewState === 'error' && (
              <View
                testID="exercise-results-error"
                className="flex-1 items-center justify-center px-5"
              >
                <Icon
                  name="CloudOff"
                  size={28}
                  className="text-text-muted mb-2"
                />
                <Text className="text-text-muted text-center">
                  {t('sportActivitySelector.error')}
                </Text>
              </View>
            )}

            {viewState === 'no-results' && (
              <View
                testID="exercise-results-empty"
                className="flex-1 items-center justify-center px-5"
              >
                <Text className="text-text-muted text-center">
                  {t('sportActivitySelector.noResults')}
                </Text>
              </View>
            )}

            {viewState === 'results' && (
              <FlatList
                data={visibleResults}
                keyExtractor={(item) => item.id}
                renderItem={renderActivityItem}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ paddingBottom: 16 }}
                ItemSeparatorComponent={renderSeparator}
                // With the search keyboard up, a tap on a row's add (+) button must
                // reach the button — not be swallowed as a keyboard-dismiss. Default
                // ('never') makes the first tap only dismiss the keyboard, so users had
                // to tap twice to add. 'handled' delivers taps to touchables directly;
                // 'on-drag' still dismisses when scrolling the results.
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode="on-drag"
              />
            )}
          </View>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

/** Stable empty array so rows without targets keep a referentially-stable prop. */
const EMPTY_TARGETS: MuscleTargetRecord[] = [];

const SKELETON_ROWS = Array.from({ length: 8 });

/**
 * Loading placeholder for the results list — mirrors the row layout with
 * `bg-muted` blocks (the app's static-skeleton convention for list rows).
 */
const ExercisePickerSkeleton = () => (
  <View testID="exercise-results-loading" className="flex-1">
    {SKELETON_ROWS.map((_, index) => (
      <View key={index} className="flex flex-row items-center gap-3 px-5 py-3">
        <View className="w-12 h-12 rounded-lg bg-muted" />
        <View className="flex-1 gap-2">
          <View className="h-4 w-32 rounded bg-muted" />
          <View className="h-3 w-20 rounded bg-muted" />
        </View>
        <View className="w-8 h-8 rounded-lg bg-muted" />
      </View>
    ))}
  </View>
);

interface SportActivityListItemProps {
  activity: SportActivityRecord;
  muscleTargets: MuscleTargetRecord[];
  category: SportActivityCategoryRecord | undefined;
  currentUserId: string | undefined;
  onSelect: (activity: SportActivityRecord) => void;
  portalHost?: string;
}

const SportActivityListItem = ({
  activity,
  muscleTargets,
  category,
  currentUserId,
  onSelect,
  portalHost,
}: SportActivityListItemProps) => {
  const { t } = useTranslation('workout');
  const { showModal } = useModal();

  const localizedName = useLocalizedValue(activity.name);
  const localizedCategoryTitle = useLocalizedValue(category?.title);

  const sortedMuscleTargets = useMemo(
    () =>
      [...muscleTargets].sort(
        (a, b) => (b.intensity ?? 0) - (a.intensity ?? 0),
      ),
    [muscleTargets],
  );

  const isOwner = !!activity.userId && activity.userId === currentUserId;

  const handleEdit = () => {
    showModal(
      SportActivityFormDialog,
      { sportActivityId: activity.id },
      { portalHost },
    );
  };

  const subtitle = buildSubtitle(
    sortedMuscleTargets,
    localizedCategoryTitle,
    t,
  );

  return (
    <View className="flex flex-row items-center gap-3 px-5 py-3">
      {/* Thumbnail */}
      <View className="flex items-center justify-center rounded-lg bg-surface-elevated w-12 h-12">
        <Icon name="Dumbbell" size={20} className="text-text-muted" />
      </View>

      {/* Content */}
      <View className="flex-1 gap-1">
        <Text
          testID="exercise-result-name"
          className="text-sm font-inter-semibold text-text-primary"
        >
          {localizedName}
        </Text>
        {subtitle && (
          <Text className="text-xs text-text-muted">{subtitle}</Text>
        )}
      </View>

      {/* Edit button (owner only) */}
      {isOwner && (
        <Pressable
          onPress={handleEdit}
          className="flex items-center justify-center rounded-lg bg-surface-elevated w-8 h-8 active:opacity-70"
        >
          <Icon name="Pencil" size={16} className="text-text-muted" />
        </Pressable>
      )}

      {/* Add button */}
      <Pressable
        testID="exercise-result-add"
        onPress={() => onSelect(activity)}
        className="flex items-center justify-center rounded-lg bg-accent w-8 h-8 active:opacity-70"
      >
        <Icon name="Plus" size={18} className="text-accent-text" />
      </Pressable>
    </View>
  );
};

function buildSubtitle(
  muscleTargets: MuscleTargetRecord[],
  categoryTitle: string | undefined,
  t: (key: string) => string,
): string | undefined {
  const parts: string[] = [];

  if (muscleTargets.length > 0) {
    parts.push(t(`muscleGroup.${muscleTargets[0].muscleGroup}`));
  }

  if (categoryTitle) {
    parts.push(categoryTitle);
  }

  return parts.join(' • ') || undefined;
}
